import { useCallback, useEffect, useRef, useState } from 'react';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../utils/safeStorage';

const STORAGE_KEY = 'forge-sidebar-width';
const COLLAPSED_WIDTH = 64;
const MIN_WIDTH = 64;
const MAX_WIDTH = 400;
const DEFAULT_WIDTH = 240;

function getInitialWidth(): number {
  const saved = safeLocalStorageGetItem(STORAGE_KEY);
  if (saved !== null) {
    const w = parseInt(saved, 10);
    if (!isNaN(w) && w >= MIN_WIDTH && w <= MAX_WIDTH) return w;
    if (w <= COLLAPSED_WIDTH) return COLLAPSED_WIDTH;
  }
  return DEFAULT_WIDTH;
}

export function useSidebar() {
  const [width, setWidth] = useState(getInitialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const expandedWidthRef = useRef(width > COLLAPSED_WIDTH ? width : DEFAULT_WIDTH);

  const isCollapsed = width <= COLLAPSED_WIDTH;

  const persist = useCallback((w: number) => {
    safeLocalStorageSetItem(STORAGE_KEY, String(w));
  }, []);

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      const w = expandedWidthRef.current;
      setWidth(w);
      persist(w);
    } else {
      if (width > COLLAPSED_WIDTH) expandedWidthRef.current = width;
      setWidth(COLLAPSED_WIDTH);
      persist(COLLAPSED_WIDTH);
    }
  }, [isCollapsed, width, persist]);

  // Drag-to-resize logic
  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      document.body.classList.add('is-resizing');

      const onMove = (ev: MouseEvent) => {
        let newWidth = ev.clientX;
        if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
        if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;
        setWidth(newWidth);
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.classList.remove('is-resizing');
        setIsResizing(false);

        setWidth((current) => {
          const final = current <= COLLAPSED_WIDTH ? COLLAPSED_WIDTH : current;
          persist(final);
          if (final > COLLAPSED_WIDTH) expandedWidthRef.current = final;
          return final;
        });
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [persist]
  );

  // Sync body class
  useEffect(() => {
    if (isResizing) {
      document.body.classList.add('is-resizing');
    } else {
      document.body.classList.remove('is-resizing');
    }
    return () => document.body.classList.remove('is-resizing');
  }, [isResizing]);

  return {
    width,
    isCollapsed,
    isResizing,
    toggleCollapse,
    onDragStart,
  } as const;
}
